import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import { ArrowForward } from '@material-ui/icons';
import SetupTable from 'components/Dashboard/WorldCup/Setup/SetupTable';
import Loading from 'components/Basic/Loading';
import SetupFormModal from 'components/Dashboard/WorldCup/Setup/SetupFormModal';
import SetupFilter from 'components/Dashboard/WorldCup/Setup/SetupFilter';
import CampaignSelector from 'components/Dashboard/WorldCup/CampaignSelector';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { useLazyGetSetupListQuery } from 'core/worldCup/setup/SetupService';
import { setSetupData } from 'core/worldCup/setup/SetupSlice';

const SetupContainer: React.FC = () => {
  const [state, setState] = useState({
    isLoading: false,
    isShowDetailModal: false,
    searchText: '',
    sortStatus: '',
    filterStatus: '',
    sortName: '',
    sortId: 'desc',
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const [getSetupList] = useLazyGetSetupListQuery();

  const { list } = useAppSelector(state => state.setupReducer);

  useEffect(() => {
    getInitialData();
    setState({ ...state, searchText: '' });
  }, []);

  useEffect(() => {
    if (selectedCampaignId) {
      setCurrent(1);
      getInitialData();
    }
  }, [selectedCampaignId]);

  useEffect(() => {
    setCurrent(1);
  }, [pageSize]);

  const getInitialData = async () => {
    setState({ ...state, isLoading: true });

    const queryString = buildQueryString({
      search: '',
      page: 1,
      size: pageSize,
      count: true,
      sort_by_id: state.sortId,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getSetupList({ queryString }).unwrap();
      dispatch(setSetupData({ list: res }));
    } catch (error) {
      console.error('Error fetching setup list:', error);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const handleChangePage = async (page: number, size: number) => {
    setState(prev => ({ ...prev, isLoading: true }));
    setPageSize(size);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: page,
      size: size,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getSetupList({ queryString }).unwrap();
      dispatch(setSetupData({ list: res }));
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (e) {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleSearch = async (e: any) => {
    const searchTextValue = e.target.value.trimStart();
    setState({ ...state, searchText: searchTextValue });
    if (e.keyCode === 13) {
      setCurrent(1);

      const queryString = buildQueryString({
        search: e.target.value.trim(),
        page: 1,
        size: pageSize,
        count: true,
        sort_by_status: state.sortStatus,
        sort_by_id: state.sortId,
        sort_by_name: state.sortName,
        filter: state.filterStatus,
        campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
      });

      try {
        const res = await getSetupList({ queryString }).unwrap();
        dispatch(setSetupData({ list: res }));
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleAvailableFilter = (value: string) => {
    setState({ ...state, filterStatus: value });
  };

  const handleSort = (value: string) => {
    let newState = { ...state };
    if (value[0] === 'I') {
      newState = {
        ...newState,
        sortId: value.substring(1),
        sortName: '',
        sortStatus: '',
      };
    } else if (value[0] === 'N') {
      newState = {
        ...newState,
        sortName: value.substring(1),
        sortId: '',
        sortStatus: '',
      };
    } else if (value[0] === 'S') {
      newState = {
        ...newState,
        sortStatus: value.substring(1),
        sortId: '',
        sortName: '',
      };
    }
    setState(newState);
  };

  const onApplyFilter = async () => {
    setState({ ...state, isLoading: true });
    setCurrent(1);

    const queryString = buildQueryString({
      search: state.searchText.trim(),
      page: 1,
      size: pageSize,
      count: true,
      sort_by_status: state.sortStatus,
      sort_by_id: state.sortId,
      sort_by_name: state.sortName,
      filter: state.filterStatus,
      campaignId: selectedCampaignId ? String(selectedCampaignId) : '',
    });

    try {
      const res = await getSetupList({ queryString }).unwrap();
      dispatch(setSetupData({ list: res }));
    } catch (e) {
      console.log(e);
    } finally {
      setState({ ...state, isLoading: false });
    }
  };

  const openModal = () => {
    setState({ ...state, isShowDetailModal: true });
  };

  return (
    <div className="setup_wrapper" id="setup_wrapper" style={{ position: 'relative' }}>
      <Loading visible={state.isLoading} />
      <CampaignSelector
        selectedCampaignId={selectedCampaignId}
        onSelect={setSelectedCampaignId}
      />
      <div className="btn_wrapper" id="new_setup_btn_wrapper">
        <Button
          className="add_btn"
          onClick={openModal}
          id="new_setup_modal_btn"
        >
          <FormattedMessage id="Add new setup" />
          <ArrowForward />
        </Button>
      </div>
      <SetupFilter
        searchText={state.searchText}
        handleSearch={handleSearch}
        onAvailableFilter={handleAvailableFilter}
        onSort={handleSort}
        onApplyFilter={onApplyFilter}
      />
      <SetupTable
        setupData={list.data || null}
        onChangePage={(current, size) => handleChangePage(current, size)}
        total={list.meta?.totalCount || 0}
        setCurrent={setCurrent}
        current={current}
        pageSize={pageSize}
        setPageSize={setPageSize}
        campaignId={selectedCampaignId}
      />
      <SetupFormModal
        mode="modal"
        isOpenModal={state.isShowDetailModal}
        onClose={() => setState({ ...state, isShowDetailModal: false })}
        onSaveSuccess={() => handleChangePage(current, pageSize)}
        setup={null}
        campaignId={selectedCampaignId}
      />
    </div>
  );
};

SetupContainer.defaultProps = {};

export default SetupContainer;

const buildQueryString = ({
  search = '',
  page = 1,
  size = 10,
  count = true,
  sort_by_id = 'desc',
  sort_by_status = '',
  sort_by_name = '',
  filter = '',
  campaignId = '',
}) => {
  const params = [`search=${encodeURIComponent(search)}`, `page=${page}`, `limit=${size}`];

  if (campaignId) {
    params.push(`campaignId=${campaignId}`);
  }

  if (sort_by_status) {
    params.push(`sort=isPublished`);
    params.push(`sortDirection=${sort_by_status}`);
  }

  if (sort_by_id) {
    params.push(`sort=id`);
    params.push(`sortDirection=${sort_by_id}`);
  }

  if (sort_by_name) {
    params.push(`sort=name`);
    params.push(`sortDirection=${sort_by_name}`);
  }

  if (filter) {
    params.push(`isPublished=${filter}`);
  }

  return params.join('&');
};
